import type { QueryClient } from '@tanstack/react-query';

import { logger } from '@/lib/logger';
import { track } from '@/lib/mixpanel';

import { createProposalClient } from '../core/ProposalClient';
import type { ProposalDetail, ProposalResolution } from '../core/types';
import { proposalQueryKeys } from '../query/keys';
import { classifyExecutionOutcome } from '../utils/classifyExecutionOutcome';
import { showExecutionFailureToast } from '../utils/showExecutionFailureToast';
import { showSuccessToast } from '../utils/showSuccessToast';

interface Entry {
  orgId: number;
  queryClient: QueryClient;
  graceTimer: ReturnType<typeof setTimeout> | null;
  // Self-scheduling poll timer. The next poll is armed only after the prior
  // request settles, so slow `executionStatus` responses never overlap.
  pollTimer: ReturnType<typeof setTimeout> | null;
  // Independent wall-clock watchdog. Because polls are serialized behind the
  // in-flight `executionStatus` request, a stalled request would otherwise
  // postpone every deadline check indefinitely; this timer guarantees the
  // MAX_WALL_MS ceiling is enforced no matter what the poll promise does.
  watchdogTimer: ReturnType<typeof setTimeout> | null;
  pollCount: number;
  startedAt: number;
  resolved: boolean;
  suppressSuccessToast: boolean;
  sawExecuting: boolean;
}

const trackers = new Map<number, Entry>();

// Resolutions that end the watch. `pending` and `executing` are both
// in-progress states — a `pending` update (e.g. a stale/out-of-order WS or
// poll response) must NOT be treated as terminal, or the tracker stops
// watching while the approval is still unresolved.
const TERMINAL_RESOLUTIONS: ReadonlySet<ProposalResolution> = new Set([
  'approved',
  'rejected',
  'failed',
  'withdrawn',
]);

const GRACE_MS = 3500;
const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 40;
const MAX_WALL_MS = 90000;
const MAX_TRACKERS = 25;

let clientFactory = createProposalClient;

export function __setClientFactoryForTesting(
  fn: typeof createProposalClient
): void {
  clientFactory = fn;
}

export function trackApproval(
  orgId: number,
  proposalId: number,
  queryClient: QueryClient,
  opts?: { suppressSuccessToast?: boolean }
): void {
  const existing = trackers.get(proposalId);
  if (existing && !existing.resolved) {
    return;
  }

  if (trackers.size >= MAX_TRACKERS) {
    // Capacity reached: the approve already fired server-side, but we cannot
    // watch this one. Surface it explicitly (metric + a toast pointing back to
    // the action) rather than dropping it silently and leaving the outcome
    // unobservable.
    logger.error(
      '[approvalTracker] max trackers reached — watch skipped',
      new Error('approval watch capacity reached'),
      { component: 'proposals', proposalId, maxTrackers: MAX_TRACKERS }
    );
    track('proposal_execution_outcome', {
      proposal_id: proposalId,
      outcome: 'watch_skipped',
    });
    // This is a failure/recovery toast, not a success toast, so it fires
    // regardless of `suppressSuccessToast` — a skipped watch must never be
    // silent, or an onboarding/pipeline/review approval could stall with no
    // recovery signal.
    showExecutionFailureToast({
      orgId,
      proposalId,
      message:
        'Approval is processing. Open the action to confirm it completed.',
    });
    return;
  }

  const entry: Entry = {
    orgId,
    queryClient,
    graceTimer: null,
    pollTimer: null,
    watchdogTimer: null,
    pollCount: 0,
    startedAt: Date.now(),
    resolved: false,
    suppressSuccessToast: !!opts?.suppressSuccessToast,
    sawExecuting: false,
  };
  trackers.set(proposalId, entry);
  entry.graceTimer = setTimeout(() => startPolling(proposalId), GRACE_MS);
  // Arm the wall-clock watchdog up front so the deadline holds even if a poll
  // request never settles. onWatchTimeout is idempotent (guards on resolved).
  entry.watchdogTimer = setTimeout(
    () => onWatchTimeout(proposalId),
    MAX_WALL_MS
  );
}

export function onProposalUpdatedWs(
  proposalId: number,
  resolution: ProposalResolution
): void {
  const e = trackers.get(proposalId);
  if (!e || e.resolved) {
    return;
  }
  if (resolution === 'executing') {
    e.sawExecuting = true;
    return;
  }
  if (resolution === 'pending' && e.sawExecuting) {
    finalizeReverted(proposalId);
    return;
  }
  if (TERMINAL_RESOLUTIONS.has(resolution)) {
    logger.info('[approvalTracker] resolved via WS', {
      component: 'proposals',
      proposalId,
      resolution,
    });
    void finalize(proposalId);
  }
}

function startPolling(proposalId: number): void {
  const entry = trackers.get(proposalId);
  if (!entry || entry.resolved) {
    return;
  }

  const poll = async () => {
    const e = trackers.get(proposalId);
    if (!e || e.resolved) {
      return;
    }
    if (Date.now() - e.startedAt >= MAX_WALL_MS || e.pollCount >= MAX_POLLS) {
      onWatchTimeout(proposalId);
      return;
    }
    e.pollCount++;
    try {
      const status = await clientFactory(e.orgId).executionStatus(proposalId);
      // Re-read live entry — a WS or timeout may have resolved it during the await.
      const current = trackers.get(proposalId);
      if (!current || current.resolved) {
        return;
      }
      if (status.resolution === 'executing') {
        current.sawExecuting = true;
      }
      if (TERMINAL_RESOLUTIONS.has(status.resolution)) {
        logger.info('[approvalTracker] resolved via poll', {
          component: 'proposals',
          proposalId,
        });
        void finalize(proposalId);
        return;
      } else if (status.resolution === 'pending' && current.sawExecuting) {
        finalizeReverted(proposalId);
        return;
      }
    } catch {
      logger.warn('[approvalTracker] poll failed, will retry until cap', {
        component: 'proposals',
        proposalId,
      });
    }
    // Arm the next poll only after this request settled — never overlap two
    // in-flight `executionStatus` calls. finalize()/onWatchTimeout() delete the
    // entry, so a resolved/timed-out watch won't reschedule.
    const rearm = trackers.get(proposalId);
    if (rearm && !rearm.resolved) {
      rearm.pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
    }
  };
  void poll();
}

function cleanup(entry: Entry): void {
  if (entry.graceTimer) {
    clearTimeout(entry.graceTimer);
  }
  if (entry.pollTimer) {
    clearTimeout(entry.pollTimer);
  }
  if (entry.watchdogTimer) {
    clearTimeout(entry.watchdogTimer);
  }
}

function resolveAndCleanup(proposalId: number, e: Entry): void {
  e.resolved = true;
  cleanup(e);
  trackers.delete(proposalId);
}

async function finalize(proposalId: number): Promise<void> {
  const e = trackers.get(proposalId);
  if (!e || e.resolved) {
    return;
  }
  resolveAndCleanup(proposalId, e);

  try {
    const detail = await clientFactory(e.orgId).get(proposalId);
    e.queryClient.setQueryData(
      proposalQueryKeys.detail(e.orgId, proposalId),
      detail
    );
    // Execution reached a terminal state (this is the WS-disconnect/polling
    // fallback path too), so refresh list views — the pending/executing list
    // membership has changed and no `proposal.updated` event is guaranteed here.
    void e.queryClient.invalidateQueries({
      queryKey: proposalQueryKeys.lists(e.orgId),
    });
    classifyAndReportExecutionOutcome(detail, e.orgId, {
      suppressSuccessToast: e.suppressSuccessToast,
    });
  } catch (err) {
    logger.error(
      '[approvalTracker] could not fetch detail to classify',
      err instanceof Error ? err : new Error(String(err)),
      { component: 'proposals', proposalId }
    );
    // This is a terminal watch result: the approval resolved (WS/poll) but the
    // outcome can't be classified because the detail fetch failed. Emit the
    // outcome metric here too — otherwise this failure path is the only
    // terminal state that never reaches execution-outcome telemetry, leaving a
    // blind spot exactly where a user saw a failure toast.
    track('proposal_execution_outcome', {
      proposal_id: proposalId,
      outcome: 'unknown',
    });
    showExecutionFailureToast({
      orgId: e.orgId,
      proposalId,
      message:
        'Could not confirm the execution result. Open the action to check its status.',
    });
  }
}

function finalizeReverted(proposalId: number): void {
  const e = trackers.get(proposalId);
  if (!e || e.resolved) {
    return;
  }
  resolveAndCleanup(proposalId, e);
  logger.warn(
    '[approvalExecutionTracker] execution reverted to pending after executing — treating as failure',
    { proposalId, orgId: e.orgId }
  );
  void e.queryClient.invalidateQueries({
    queryKey: proposalQueryKeys.detail(e.orgId, proposalId),
  });
  void e.queryClient.invalidateQueries({
    queryKey: proposalQueryKeys.lists(e.orgId),
  });
  track('proposal_execution_outcome', {
    proposal_id: proposalId,
    outcome: 'execution_failed',
  });
  showExecutionFailureToast({
    orgId: e.orgId,
    proposalId,
    message: "This action didn't go through. Open it to review and try again.",
  });
}

function onWatchTimeout(proposalId: number): void {
  const e = trackers.get(proposalId);
  if (!e || e.resolved) {
    return;
  }
  resolveAndCleanup(proposalId, e);

  logger.error(
    '[approvalTracker] watch timed out',
    new Error('approval watch cap reached'),
    { component: 'proposals', proposalId }
  );
  track('proposal_execution_outcome', {
    proposal_id: proposalId,
    outcome: 'timeout',
  });
  showExecutionFailureToast({
    orgId: e.orgId,
    proposalId,
    message:
      'This is taking longer than expected. Open the action to check its status.',
  });
}

/**
 * Classifies a terminal proposal's execution outcome, emits the
 * `proposal_execution_outcome` telemetry event, and shows the corresponding
 * success/failure toast — all three happen here, not just classification.
 */
export function classifyAndReportExecutionOutcome(
  proposal: ProposalDetail,
  orgId: number,
  opts?: { suppressSuccessToast?: boolean }
): void {
  const outcome = classifyExecutionOutcome(proposal);

  // The success path is ONLY correct for an APPROVED execution. finalize() runs
  // on any non-`executing` terminal resolution — approved, failed, rejected, OR
  // withdrawn — and classifyExecutionOutcome returns `success` whenever
  // `execution_result` carries no error (including when it is null). Without the
  // resolution gate, a rejected/withdrawn proposal, or one that `failed` with a
  // null `execution_result`, would slip through and show a misleading success
  // toast. Gate success on `resolution === 'approved'`; every other terminal
  // gets the persistent failure/recovery toast so the user is never told an
  // action succeeded when it did not.
  const succeeded =
    outcome.status === 'success' && proposal.resolution === 'approved';

  let outcomeLabel: string;
  if (succeeded) {
    outcomeLabel = 'success';
  } else if (outcome.status === 'failure') {
    outcomeLabel = 'failure';
  } else {
    // Non-approved terminal with no classified execution error (rejected,
    // withdrawn, or failed-without-result). Record the actual resolution so the
    // metric doesn't misreport it as a success.
    outcomeLabel = proposal.resolution;
  }
  track('proposal_execution_outcome', {
    proposal_id: proposal.id,
    outcome: outcomeLabel,
  });

  if (succeeded) {
    if (!opts?.suppressSuccessToast) {
      showSuccessToast(proposal);
    }
    return;
  }

  // Prefer the classified failure message; otherwise fall back to a neutral
  // recovery message for a non-approved terminal (a persistent toast either
  // way, since it may be the only path back to the action after navigation).
  const message =
    outcome.status === 'failure'
      ? outcome.message
      : 'Couldn’t confirm this action was approved. Open the action to check its status.';
  logger.warn('[approvalTracker] execution did not succeed', {
    component: 'proposals',
    proposalId: proposal.id,
    resolution: proposal.resolution,
    message,
  });
  showExecutionFailureToast({
    orgId,
    proposalId: proposal.id,
    proposalTitle: proposal.title,
    message,
  });
}

export function __resetForTesting(): void {
  trackers.forEach(e => cleanup(e));
  trackers.clear();
  clientFactory = createProposalClient;
}
